import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  output as output_1,
  signal,
} from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { unparse } from 'papaparse';
import {
  BdUiFileInputComponent,
  BdUiFileInputConfig,
} from '@bindoc-projects/ui-lib/file/ui';
import { KostDataImportProcessData } from '../../import-process';
import { KostDataImportNewDatasetInputResultComponent } from './result/result.component';
import { KostDataImportNewDatasetInputResultLogComponent } from './log';
import type { KostDataImportDatasetStatus } from './log/dataset-steps';
import {
  KostDataImportDatasetInputProgress,
  KostDataImportDatasetInputProcessor,
  KostDataImportDatasetInputProcessorResult,
} from '@bindoc-projects/kost-data-import-domain-lib';

interface InputResult {
  input: File;
  output: {
    errors?: string[];
    transformed: Blob;
    additionalInformation: unknown;
    additionalArtifacts: { content: File | Blob; displayName: string }[];
  };
}

@Component({
  selector: 'kost-data-import-new-dataset-input',
  template: `
    @if (!loading() && !result().input) {
      <div>
        <span i18n>Wählen Sie ihre Datei aus</span>
      </div>
    } @else {
      <div>
        <span i18n>Aktuell ausgelesene Datei</span>
      </div>
    }

    <div class="bd-container">
      <bd-ui-file-input
        [config]="config.fileInput"
        (fileChange)="onFileChange($event)"
      >
      </bd-ui-file-input>
    </div>

    <kost-data-import-new-dataset-input-result-log
      [progress$]="progress$"
      [steps]="config.steps"
      [status]="status()"
    >
    </kost-data-import-new-dataset-input-result-log>

    @if (!loading() && !!result().input) {
      <kost-data-import-new-dataset-input-result [data]="result()">
      </kost-data-import-new-dataset-input-result>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BdUiFileInputComponent,
    KostDataImportNewDatasetInputResultComponent,
    KostDataImportNewDatasetInputResultLogComponent,
  ],
})
export class KostDataImportNewDatasetInputComponent {
  @Input()
  public config: {
    fileInput: BdUiFileInputConfig;
    useWorker: boolean;
    /** The processor's steps in order, step id → label. */
    steps: Readonly<Record<string, string>>;
  };

  /**
   * The web worker logic needs to be moved to app, because we can't send the process function to
   * a generic worker.
   */
  @Input()
  public processor: (() => Worker) | KostDataImportDatasetInputProcessor;

  @Input()
  public processData: KostDataImportProcessData;

  public readonly update = output_1<{
    input: File;
    output: {
      transformed: Blob;
      additionalInformation: unknown;
      additionalArtifacts: {
        content: File | Blob;
        displayName: string;
      }[];
    };
  }>();

  // Signal-backed state so worker.onmessage updates flow through Angular's
  // reactive graph and trigger CD without depending on zone-patching of the
  // Worker callback (which is unreliable today and will not exist in the
  // upcoming zoneless setup).
  public readonly loading = signal(false);
  public readonly result = signal<InputResult>({
    input: null,
    output: {
      errors: null,
      additionalArtifacts: null,
      additionalInformation: null,
      transformed: null,
    },
  });

  public progress$: Subject<KostDataImportDatasetInputProgress> =
    new ReplaySubject(1);

  /** Where the processing of the selected file stands. */
  public readonly status = computed((): KostDataImportDatasetStatus => {
    const { input, output } = this.result();
    if (this.loading()) {
      return 'running';
    }
    if (!input) {
      return 'idle';
    }
    if (output?.errors?.length) {
      return 'failed';
    }
    return output?.transformed ? 'done' : 'idle';
  });

  public async onFileChange(file: File) {
    if (!file) {
      this._reset();
    } else {
      // A fresh stream per file, so the step log starts over.
      this.progress$.complete();
      this.progress$ = new ReplaySubject(1);
      this.loading.set(true);
      this.result.set({
        input: file,
        output: {
          transformed: null,
          additionalArtifacts: null,
          additionalInformation: null,
        },
      });

      this.progress$.next({
        currentStep: `Verarbeitung von ${file.name} startet`,
        progress: 0,
        type: 'STEP_UPDATE',
      });

      if (this._isWorkerFactory(this.processor)) {
        this._createWorker(this.processor, file);
      } else {
        const result = await this.processor
          .process(
            file,
            this.processData.parameters,
            this.processData.dependencies,
          )
          .catch((err: unknown) => {
            console.error('Unbekannter Fehler in Verarbeitung', err);

            return {
              errors: [
                err && err['message']
                  ? err['message']
                  : 'Unbekannter Fehler in Verarbeitung',
              ],
            };
          });

        this.progress$ = this.processor.progressHandler.progress$;

        this._onProcessorComplete(result);
      }
    }
  }

  private _reset() {
    if (this.progress$) {
      this.progress$.complete();
      this.progress$ = new ReplaySubject(1);
    }

    this.result.update((prev) => ({ ...prev, input: null }));
    this.processData.data.input = null;
    this.processData.data.output = null;

    const result: KostDataImportDatasetInputProcessorResult = {
      transformed: null,
      errors: [],
      anonymizationTable: null,
      additionalInformation: {},
      locations: null,
    };

    this._onProcessorComplete(result);
  }

  private _createWorker(workerFactory: () => Worker, file: File) {
    const worker = workerFactory();

    worker.onmessage = ({ data }) => {
      if (data.type === 'progress') {
        this.progress$.next(data.payload);
      } else if (data.type === 'result') {
        this._onProcessorComplete(data.payload);
        worker.terminate();
      }
    };

    worker.postMessage({
      file,
      parameters: this.processData.parameters,
      dependencies: this.processData.dependencies,
    });
  }

  private _onProcessorComplete(
    processorResult: KostDataImportDatasetInputProcessorResult,
  ) {
    // Clear the loading state first so the result/error template can render even
    // if anonymization-table CSV generation below fails on partial data.
    this.loading.set(false);

    const additionalArtifacts: InputResult['output']['additionalArtifacts'] =
      [];

    if (processorResult.anonymizationTable) {
      try {
        additionalArtifacts.push({
          content: this._createCsvBlob(processorResult.anonymizationTable),
          displayName: 'Anonymisierungstabelle',
        });
      } catch (err) {
        console.error(
          'Anonymisierungstabelle konnte nicht erzeugt werden',
          err,
        );
      }
    }

    this.result.update((prev) => ({
      ...prev,
      output: {
        errors: processorResult.errors,
        transformed: processorResult.transformed,
        additionalInformation: processorResult.additionalInformation,
        additionalArtifacts,
      },
    }));

    if (!processorResult.errors || processorResult.errors.length === 0) {
      this.update.emit(this.result());
    }
  }

  private _createCsvBlob(data: string[][]): Blob {
    // Delegate the CSV serialization to papaparse so quoting/escaping of
    // embedded delimiters, quote characters and line breaks is handled
    // correctly (RFC 4180). German-locale Excel uses ';' as its default field
    // separator, so use ';' and prepend a UTF-8 BOM so Excel reliably detects
    // the encoding and the separator. escapeFormulae guards the downloaded file
    // against CSV injection (cells starting with =, +, -, @). (BDA-5640)
    const BOM = '\uFEFF';
    const csvContent = unparse(data, {
      delimiter: ';',
      quotes: true,
      newline: '\r\n',
      escapeFormulae: true,
    });

    return new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  }

  private _isWorkerFactory(
    input: (() => Worker) | KostDataImportDatasetInputProcessor,
  ): input is () => Worker {
    return typeof input === 'function';
  }
}
